import { invisiblyApi } from '../../api/invisibly-api';
import { InvisiblyApiResponse } from "../../../services/api/api-response.types";
import ReduxService from '../../../redux/redux.service';
import { apiUrl } from '../../../constants/config';
import {
  AccountsResponse,
  AccountType,
  AwpCompletesRow,
  CampaignsResponse,
  CampaignType,
  ReportMetrics,
  RawDataPoint,
  TableDataPoint,
  HourlyDataPoint,
  LineItemType,
  LineItemResponse,
} from './reporting.types';
import { ReduxState } from 'src/redux/redux-state';

const getIsClientView = (): boolean => {
  const state = ReduxService.Store.getState() as ReduxState;
  const userGroups = state?.userState?.permissions?.groups || [];
  return userGroups.includes('client_user') || userGroups.includes('client_admin');
};

const getApiPrefixAndParams = (previewClientId?: string | null) => {
  if (previewClientId) {
    return { prefix: 'client', params: { ext_client_id: previewClientId } };
  }
  const isClient = getIsClientView();
  return { prefix: isClient ? 'client' : 'b2b', params: {} };
};

const reportingEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getPlatforms: builder.query<string[], { previewClientId?: string }>({
      query: (arg) => {
        const previewClientId = arg?.previewClientId;
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          method: 'get',
          url: `${prefix}/reporter/filter/sources`,
          ...(Object.keys(params).length && { params }),
        };
      },
      transformResponse(response: InvisiblyApiResponse<{ sources: string[] }>) {
        return response.item.sources;
      },
    }),
    getAccountsByPlatform: builder.query<AccountType[], { source: string; previewClientId?: string }>({
      query: ({ source, previewClientId }) => {
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          method: 'get',
          url: `${prefix}/reporter/filter/sources/${source}/accounts`,
          ...(Object.keys(params).length && { params }),
        };
      },
      transformResponse(response: InvisiblyApiResponse<AccountsResponse>) {
        return response.item.account;
      },
    }),
    getCampaignsByAccount: builder.query<CampaignType[], { source: string; accountId: number[]; previewClientId?: string }>({
      query: ({ source, accountId, previewClientId }) => {
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          method: 'get',
          url: `${prefix}/reporter/filter/sources/${source}/accounts/${accountId}/campaigns`,
          ...(Object.keys(params).length && { params }),
        };
      },
      transformResponse(response: InvisiblyApiResponse<CampaignsResponse>) {
        return response.item.campaigns;
      },
    }),
    getLineItemsByCampaignAndAccount: builder.query<LineItemType[], { source: string; accountId: number[]; campaignId: number[]; previewClientId?: string }>({
      query: ({ source, accountId, campaignId, previewClientId }) => {
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          method: 'get',
          url: `${prefix}/reporter/filter/sources/${source}/accounts/${accountId}/campaigns/${campaignId}/lines`,
          ...(Object.keys(params).length && { params }),
        };
      },
      transformResponse(response: InvisiblyApiResponse<LineItemResponse>) {
        return response.item.line_items;
      },
    }),
    getReportMetrics: builder.mutation<ReportMetrics, { source: string; account_ids: number[]; campaign_ids?: number[]; line_item_ids?: number[]; start_date: string; end_date: string; previewClientId?: string }>({
      query: (data) => {
        const { previewClientId, ...rest } = data;
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          data: { ...rest, report_type: 'metrics', download: false },
          method: 'POST',
          url: `${prefix}/reporter`,
          ...(Object.keys(params).length && { params }),
        };
      },
      transformResponse(response: InvisiblyApiResponse<ReportMetrics>) {
        return response.item;
      },
    }),
    getTimeSeriesData: builder.mutation<RawDataPoint[], { source: string; account_ids: number[]; campaign_ids?: number[]; line_item_ids?: number[]; start_date: string; end_date: string; previewClientId?: string }>({
      query: (data) => {
        const { previewClientId, ...rest } = data;
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          data: { ...rest, report_type: 'timeseries', download: false },
          method: 'POST',
          url: `${prefix}/reporter`,
          ...(Object.keys(params).length && { params }),
        };
      },
      transformResponse(response: InvisiblyApiResponse<RawDataPoint[]>) {
        return response.item;
      },
    }),
    getBreakdownData: builder.mutation<RawDataPoint[], { source: string; account_ids: number[]; campaign_ids?: number[]; line_item_ids?: number[]; start_date: string; end_date: string; previewClientId?: string }>({
      query: (data) => {
        const { previewClientId, ...rest } = data;
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          data: { ...rest, report_type: 'breakdown', download: false },
          method: 'POST',
          url: `${prefix}/reporter`,
          ...(Object.keys(params).length && { params }),
        };
      },
      transformResponse(response: InvisiblyApiResponse<RawDataPoint[]>) {
        return response.item;
      },
    }),
    getGeoData: builder.mutation<TableDataPoint[], { source: string; account_ids: number[]; campaign_ids?: number[]; line_item_ids?: number[]; start_date: string; end_date: string; previewClientId?: string }>({
      query: (data) => {
        const { previewClientId, ...rest } = data;
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          data: { ...rest, report_type: 'geo', download: false },
          method: 'POST',
          url: `${prefix}/reporter`,
          // NOTE: Skip displaying error banners until geo data is supported across all platforms
          handleErrorGlobally: false,
          params: { ...params, field: 'zipcode', amount: 50 },
        };
      },
      transformResponse(response: InvisiblyApiResponse<any[]>) {
        return response.item.map((item: any) => ({
          ...item,
          key: item.zipcode,
        }));
      },
    }),
    getDomainData: builder.mutation<TableDataPoint[], { source: string; account_ids: number[]; campaign_ids?: number[]; line_item_ids?: number[]; start_date: string; end_date: string; previewClientId?: string }>({
      query: (data) => {
        const { previewClientId, ...rest } = data;
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          data: { ...rest, report_type: 'domain', download: false },
          method: 'POST',
          url: `${prefix}/reporter`,
          // NOTE: Skip displaying error banners until domain data is supported across all platforms
          handleErrorGlobally: false,
          params: { ...params, field: 'domain', amount: 20 },
        };
      },
      transformResponse(response: InvisiblyApiResponse<any[]>) {
        return response.item.map((item: any) => ({
          ...item,
          key: item.domain,
        }));
      },
    }),
    getAppData: builder.mutation<TableDataPoint[], { source: string; account_ids: number[]; campaign_ids?: number[]; line_item_ids?: number[]; start_date: string; end_date: string; previewClientId?: string }>({
      query: (data) => {
        const { previewClientId, ...rest } = data;
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          data: { ...rest, report_type: 'domain', download: false },
          method: 'POST',
          url: `${prefix}/reporter`,
          // NOTE: Skip displaying error banners until app data is supported across all platforms
          handleErrorGlobally: false,
          params: { ...params, field: 'app', amount: 20 },
        };
      },
      transformResponse(response: InvisiblyApiResponse<any[]>) {
        return response.item.map((item: any) => ({
          ...item,
          key: item.app,
        }));
      },
    }),
    getDmaData: builder.mutation<TableDataPoint[], { source: string; account_ids: number[]; campaign_ids?: number[]; line_item_ids?: number[]; start_date: string; end_date: string; previewClientId?: string }>({
      query: (data) => {
        const { previewClientId, ...rest } = data;
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          data: { ...rest, report_type: 'dma', download: false },
          method: 'POST',
          url: `${prefix}/reporter`,
          // NOTE: Skip displaying error banners until app data is supported across all platforms
          handleErrorGlobally: false,
          params: { ...params, field: 'dma', amount: 20 },
        };
      },
      transformResponse(response: InvisiblyApiResponse<any[]>) {
        return response.item.map((item: any) => ({
          ...item,
          key: item.dma,
        }));
      },
    }),
    getHourlyData: builder.mutation<HourlyDataPoint[], { source: string; account_ids: number[]; campaign_ids?: number[]; line_item_ids?: number[]; start_date: string; end_date: string; previewClientId?: string }>({
      query: (data) => {
        const { previewClientId, ...rest } = data;
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          data: { ...rest, report_type: 'by_hour', download: false },
          method: 'POST',
          url: `${prefix}/reporter`,
          // NOTE: Skip displaying error banners until hourly data is supported across all platforms
          handleErrorGlobally: false,
          ...(Object.keys(params).length && { params }),
        };
      },
      transformResponse(response: InvisiblyApiResponse<HourlyDataPoint[]>) {
        return response.item;
      },
    }),
    getAwpCompletesData: builder.mutation<AwpCompletesRow[], { source: string; account_ids: number[]; campaign_names: string[]; start_date: string; end_date: string; previewClientId?: string }>({
      query: (data) => {
        const { previewClientId, ...rest } = data;
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          data: { ...rest, report_type: 'awp', download: false },
          method: 'POST',
          url: `${prefix}/reporter`,
          handleErrorGlobally: false,
          ...(Object.keys(params).length && { params }),
        };
      },
      transformResponse(response: InvisiblyApiResponse<AwpCompletesRow | AwpCompletesRow[]>) {
        const item = response.item;
        if (Array.isArray(item)) return item as AwpCompletesRow[];
        if (item != null && typeof item === 'object' && 'completes' in item) {
          return [item as AwpCompletesRow];
        }
        return [];
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetPlatformsQuery,
  useGetAccountsByPlatformQuery,
  useGetCampaignsByAccountQuery,
  useGetLineItemsByCampaignAndAccountQuery,
  useGetReportMetricsMutation,
  useGetTimeSeriesDataMutation,
  useGetBreakdownDataMutation,
  useGetGeoDataMutation,
  useGetDomainDataMutation,
  useGetAppDataMutation,
  useGetDmaDataMutation,
  useGetHourlyDataMutation,
  useGetAwpCompletesDataMutation,
} = reportingEndpoints;

// Endpoints without status code return handled separately from RTK
export async function downloadReportFile(data: { source: string; account_ids: number[]; campaign_ids?: number[]; start_date: string; end_date: string }): Promise<Blob | null> {
  try {
    const state = ReduxService.Store.getState();
    const jwt = state?.userState?.access;
    const url = `${getIsClientView() ? 'client' : 'b2b'}/reporter`;

    const response = await fetch(`${apiUrl}/${url}`, {
      method: 'POST',
      headers: jwt ? {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      } : {},
      body: JSON.stringify({...data, report_type: 'raw', download: true}),
    });

    // Check if the response is JSON (error) or blob (success)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const jsonResponse = await response.json();
      if (jsonResponse.status?.status_type === false) {
        console.error(`Failed to download report: ${jsonResponse.status.status_message} (${jsonResponse.status.status_code})`);
        return null;
      }
    }

    if (!response.ok) {
      console.error(`Failed to download report. Status: ${response.status} - ${response.statusText}`);
      return null;
    }

    const blob = await response.blob();
    if (blob.size === 0) {
      console.error('No data available for download');
      return null;
    }

    return blob;
  } catch (error) {
    console.error('Error occurred while downloading report:', error);
    return null;
  }
}